import { useCallback, useEffect, useRef } from 'react';

import { MessageType, OrderShort, SdkMessage } from '@solidgate/react-sdk';
import { useTranslation } from 'react-i18next';

import useErrors from '~/hooks/useErrors';
import { AnalyticPaymentMethods, PAYMENT_METHODS } from '~/types/payment';

import {
  handleAlreadyProcessed,
  handlePaypalPayment,
  renderPaypalButton,
} from '../paypalMethods';
import { PaymentApi } from '../types';

type UsePaypalLogicOptions = {
  paymentApi: PaymentApi;
  onPaymentSuccess: (order: OrderShort) => void | Promise<void>;
  onPaymentFail: (message: string, method: AnalyticPaymentMethods) => void;
  onPaymentSubmit: (method: AnalyticPaymentMethods) => void;
  onMounted: (event: SdkMessage[MessageType.Mounted]) => void;
};

type UsePaypalLogicReturn = {
  paypalBtnRef: React.RefObject<HTMLDivElement | null>;
};

const usePaypalLogic = ({
  paymentApi,
  onPaymentSuccess,
  onPaymentFail,
  onPaymentSubmit,
  onMounted,
}: UsePaypalLogicOptions): UsePaypalLogicReturn => {
  const { t } = useTranslation('general');
  const { reportUserErrors } = useErrors();

  const paypalBtnRef = useRef<HTMLDivElement | null>(null);
  const paypalIntentAbortController = useRef<AbortController | null>(null);

  const initializePaypal = useCallback(async (): Promise<void> => {
    if (!paymentApi.createPaymentIntentPaypal) {
      return;
    }

    paypalIntentAbortController.current?.abort('initializePaypal');
    const newAbortController = new AbortController();
    paypalIntentAbortController.current = newAbortController;

    try {
      const paypalData = await paymentApi.createPaymentIntentPaypal();
      if (!newAbortController.signal.aborted)
        renderPaypalButton(paypalData.pay_form.script_url);
    } catch (e: any) {
      const isPaymentError = e.message.includes('createPaymentIntentPaypal');
      reportUserErrors({
        error: e,
        method: 'createPaymentIntentPaypal',
        userMessage: isPaymentError
          ? t(
              'error:create_payment_intent_paypal',
              'Paypal payment initiation unsuccessful. Try again or',
            )
          : e.error || e.message || 'Error :(',
      });
    }
  }, [paymentApi.createPaymentIntentPaypal]);

  const reinitializePaypal = useCallback(async (): Promise<void> => {
    initializePaypal();
  }, [initializePaypal]);

  useEffect(() => {
    initializePaypal();

    return (): void => {
      paypalIntentAbortController.current?.abort();
    };
  }, [initializePaypal]);

  useEffect(() => {
    paypalBtnRef.current?.addEventListener('order-started-processing', () =>
      onPaymentSubmit(PAYMENT_METHODS.PAYPAL),
    );

    paypalBtnRef.current?.addEventListener(
      'order-processed',
      handlePaypalPayment,
    );

    paypalBtnRef.current?.addEventListener(
      'order-already-processed',
      handleAlreadyProcessed,
    );

    paypalBtnRef.current?.addEventListener('button-ready', () =>
      onMounted({ entity: 'paypal', type: MessageType.Mounted }),
    );

    return (): void => {
      paypalBtnRef.current?.removeEventListener(
        'order-started-processing',
        () => onPaymentSubmit(PAYMENT_METHODS.PAYPAL),
      );

      paypalBtnRef.current?.removeEventListener(
        'order-processed',
        handlePaypalPayment,
      );

      paypalBtnRef.current?.removeEventListener(
        'order-already-processed',
        handleAlreadyProcessed,
      );

      paypalBtnRef.current?.removeEventListener('button-ready', () =>
        onMounted({ entity: 'paypal', type: MessageType.Mounted }),
      );
    };
  }, [paypalBtnRef.current]);

  useEffect(() => {
    const handlePaypalIframeEvent = async (
      mes: Record<string, any>,
    ): Promise<void> => {
      const { payment_status, method, error_message, order } = mes.data;

      if (payment_status === 'error') {
        await onPaymentFail(error_message, method);
        await reinitializePaypal();
      }

      if (payment_status === 'info' || payment_status === 'success') {
        await onPaymentSuccess(order);
      }
    };

    window.addEventListener('message', handlePaypalIframeEvent);

    return (): void => {
      window.removeEventListener('message', handlePaypalIframeEvent);
    };
  }, []);

  return {
    paypalBtnRef,
  };
};

export default usePaypalLogic;
